import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, ConfirmDialog, ErrorState, Icon, IconButton, MenuDropdown, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { DocPreview, siteText, Stepper } from '../../../components/common/DocPreview/DocPreview';
import { PartyCard } from '../../../components/common/PartyCard/PartyCard';
import { ShareMenu } from '../../../components/common/ShareMenu/ShareMenu';
import { HistoryCard, KeyValues, StatusBadge, TotalsBox, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { QUOTATION_STATUS } from '../../../constants/status';
import { masterLabel } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { quotationService } from '../services/quotationService';

const STEPS = [
  { key: 'draft', label: labelOf(QUOTATION_STATUS, 'draft'), icon: 'quote' },
  { key: 'sent', label: labelOf(QUOTATION_STATUS, 'sent'), icon: 'send' },
  { key: 'approved', label: labelOf(QUOTATION_STATUS, 'approved'), icon: 'checkCircle' },
  { key: 'converted', label: labelOf(QUOTATION_STATUS, 'converted'), icon: 'wrench' },
];
const STEP_INDEX = { draft: 0, sent: 1, expired: 1, rejected: 1, approved: 2, converted: 3 };

const daysLeft = (d: string | Date) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

/** Area and city of a site, e.g. "B-12, Mayur Colony, Kothrud" + "Pune" -> "Kothrud, Pune". */
function placeOf(a?: { line?: string; city?: string } | null): string {
  if (!a) return '';
  const area = (a.line ?? '').split(',').map((p) => p.trim()).filter(Boolean).pop() ?? '';
  const city = (a.city ?? '').trim();
  return [area, city].filter((v, i, all) => v && all.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i).join(', ');
}

export default function QuotationDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useMasters();
  const { data: q, error, reload } = useFetch(() => quotationService.get(id), [id]);
  useCrumb(`/quotations/${id}`, q?.quotationNo);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!q) return <PageLoader />;
  const place = placeOf(q.siteAddress?.line || q.siteAddress?.city ? q.siteAddress : q.customer?.address);

  const status = async (next, message) => {
    if (await run(next, () => quotationService.setStatus(id, next), message)) reload();
  };
  const duplicate = async () => {
    const copy = await run('duplicate', () => quotationService.duplicate(id), 'Copy made as a new draft');
    if (copy && copy !== true) navigate(`/quotations/${copy._id}`);
  };
  const remove = async () => {
    if (await run('delete', () => quotationService.remove(id), `${q.quotationNo} deleted`)) navigate('/quotations', { replace: true });
    setConfirmDelete(false);
  };
  const pdf = (which: 'pdf' | 'material-list-pdf', key: string) =>
    run(key, () => (which === 'pdf' ? quotationService.openPdf(id) : quotationService.openMaterialListPdf(id)));

  const editable = ['draft', 'sent'].includes(q.status);
  const primary = {
    draft: <Button leftIcon="send" loading={busy === 'sent'} onClick={() => status('sent', 'Marked as sent')}>Mark as sent</Button>,
    sent: <Button leftIcon="checkCircle" loading={busy === 'approved'} onClick={() => status('approved', 'Marked as accepted')}>Mark accepted</Button>,
    approved: <Button leftIcon="wrench" onClick={() => navigate(`/quotations/${id}/convert`)}>Create job</Button>,
    rejected: <Button leftIcon="refresh" loading={busy === 'draft'} onClick={() => status('draft', 'Moved back to draft')}>Back to draft</Button>,
    expired: <Button leftIcon="send" loading={busy === 'sent'} onClick={() => status('sent', 'Marked as sent again')}>Send again</Button>,
    converted: q.serviceId && <Button leftIcon="wrench" onClick={() => navigate(`/jobs/${q.serviceId}`)}>Open job</Button>,
  }[q.status];

  const more = [
    q.status === 'draft' && { label: 'Mark accepted', icon: 'checkCircle', onClick: () => status('approved', 'Marked as accepted') },
    ['draft', 'sent', 'approved'].includes(q.status) && { label: 'Mark rejected', icon: 'x', onClick: () => status('rejected', 'Marked as rejected') },
    q.status === 'sent' && { label: 'Mark expired', icon: 'clock', onClick: () => status('expired', 'Marked as expired') },
    ['sent', 'expired'].includes(q.status) && { label: 'Back to draft', icon: 'refresh', onClick: () => status('draft', 'Moved back to draft') },
    q.status === 'approved' && { label: 'Back to sent', icon: 'send', onClick: () => status('sent', 'Moved back to sent') },
    { label: 'Duplicate as new draft', icon: 'quote', onClick: duplicate },
    { label: 'Material list PDF', icon: 'download', onClick: () => pdf('material-list-pdf', 'mpdf') },
    q.status === 'draft' && { divider: true },
    q.status === 'draft' && { label: 'Delete quotation', icon: 'trash', danger: true, onClick: () => setConfirmDelete(true) },
  ].filter(Boolean);

  const validTill = q.validTill ?? q.date;
  const left = daysLeft(validTill);
  const validity = Math.max(0, Math.min(100, (left / Math.max(1, daysLeft(validTill) - daysLeft(q.date))) * 100));
  const lines = (arr, textKey) =>
    arr.map((l) => ({
      key: l._id,
      text: l[textKey],
      sub: [l.brand, l.specification, l.sacHsn && `HSN ${l.sacHsn}`].filter(Boolean).join(' · '),
      qty: l.qty,
      unit: l.unit,
      rate: l.rate,
      gstRate: l.gstRate,
      amount: l.amount,
      byCustomer: l.supplyBy === 'customer',
    }));

  return (
    <>
      <PageHeader
        title={q.quotationNo}
        badge={<StatusBadge map={QUOTATION_STATUS} value={q.status} />}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><Icon name="user" size={15} />{q.customer?.name}</span>
            {place && (
              <span className="inline-flex min-w-0 items-center gap-1.5" title={siteText(q.siteAddress ?? q.customer?.address)}>
                <Icon name="mapPin" size={15} className="shrink-0" />
                <span className="truncate">{place}</span>
              </span>
            )}
            <span className="tabular inline-flex items-center gap-1.5"><Icon name="rupee" size={15} />{money(q.grandTotal)}</span>
          </span>
        }
        actions={
          <>
            {editable && <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/quotations/${id}/edit`)}>Edit</Button>}
            <Button variant="outline" leftIcon="download" loading={busy === 'pdf'} onClick={() => pdf('pdf', 'pdf')}>Print / PDF</Button>
            <ShareMenu
              kind="quotations"
              id={id}
              docNo={q.quotationNo}
              phone={q.customer?.phone}
              shared={!!q.shareToken}
              onChange={reload}
              message={`Hello ${q.customer?.name}, please find our quotation ${q.quotationNo}${q.title ? ` for ${q.title}` : ''}: ${money(q.grandTotal)}.`}
            />
            <MenuDropdown trigger={<IconButton icon="more" label="More actions" variant="outline" />} items={more} />
            {primary}
          </>
        }
      />

      <div className="space-y-5">
        <Stepper steps={STEPS} current={STEP_INDEX[q.status]} tone={q.status === 'rejected' ? 'red' : 'brand'} />
        {['rejected', 'expired'].includes(q.status) && (
          <p className={cn('rounded-lg border px-4 py-3 text-sm', q.status === 'rejected' ? 'border-red-200 bg-red-50 text-red-700' : 'border-sun-200 bg-sun-50 text-sun-700')}>
            This quotation is {labelOf(QUOTATION_STATUS, q.status).toLowerCase()}. Duplicate it to send a revised one.
          </p>
        )}

        <TwoColumn
          main={
            <DocPreview
              kind="Quotation"
              number={q.quotationNo}
              party={q.customer}
              job={{ title: q.title || masterLabel('serviceType', q.serviceType), site: siteText(q.siteAddress) }}
              dates={[
                ['Date', formatDate(q.date)],
                ['Valid until', formatDate(q.validTill)],
                ['Place of supply', 'Maharashtra'],
                ['Work type', masterLabel('serviceType', q.serviceType)],
              ]}
              isGst={q.isGst}
              sections={[
                { title: 'Fitting & labour', lines: lines(q.labourItems, 'description') },
                { title: 'Material list', lines: lines(q.materialItems, 'name') },
              ]}
              terms={q.terms}
              totals={
                <TotalsBox
                  totals={q}
                  isGst={q.isGst}
                  lead={[
                    ['Fitting & labour', money(q.labourTotal)],
                    ['Material by Electroz', money(q.materialTotal)],
                  ]}
                />
              }
            />
          }
          aside={
            <>
              <Card>
                <CardHeader title="Summary" />
                <div className="p-5 pt-4">
                  <KeyValues rows={[['Total', money(q.grandTotal), { strong: true }], ['GST', q.isGst ? money(q.totalTax) : 'No GST'], ['Validity', left >= 0 ? `${left} days left` : 'Expired']]} />
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-navy-100" aria-hidden="true">
                    <div className={cn('h-full rounded-full', left < 3 ? 'bg-red-500' : 'bg-brand-600')} style={{ width: `${validity}%` }} />
                  </div>
                  {q.notes && <p className="mt-4 rounded-lg bg-navy-50 p-3 text-[13px] text-ink-600">{q.notes}</p>}
                </div>
              </Card>
              <PartyCard party={q.customer} to={`/customers/${q.customerId}`} whatsappText={`Hello ${q.customer?.name}, here is quotation ${q.quotationNo} for ${money(q.grandTotal)}.`} />
              <HistoryCard history={q.statusHistory} statusMap={QUOTATION_STATUS} />
            </>
          }
        />
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        loading={busy === 'delete'}
        danger
        title="Delete this draft?"
        message="The quotation will be removed for good."
        confirmLabel="Delete quotation"
      />
    </>
  );
}
